import { createReducer } from 'react-use';
import thunk from 'redux-thunk';
import { transactionsReducer } from './transactionsReducer';
import { TxDispatch, trackTxAction } from './actions';
import { TxState } from './TxState';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { LocalStorageTxStore } from './storage/LocalStorageTxStore';
import { createTxStoreMiddleware } from './storage';
import {
  pendingSubject,
  completedSubject,
  cancelledSubject,
  failedSubject,
} from './rx';
import { useLCDClient } from '@terra-money/wallet-provider';
import {
  CompletedTransaction,
  FailedTransaction,
  PendingTransaction,
  TransactionStatus,
} from './types';
import { UIElementProps } from 'components/UIElementProps';

const storage = new LocalStorageTxStore('__tx_store');

const useThunkReducer = createReducer<any, TxState>(
  thunk.withExtraArgument({
    pending: pendingSubject,
    completed: completedSubject,
    cancelled: cancelledSubject,
    failed: failedSubject,
  }) as any,
  createTxStoreMiddleware(storage)
);

const initialState = {
  initialized: false,
  transactions: [],
};

type TxEventHandlers = {
  onPending?: (transaction: PendingTransaction) => void;
  onCompleted?: (transaction: CompletedTransaction) => void;
  onCancelled?: (transaction: PendingTransaction) => void;
  onFailed?: (transaction: FailedTransaction) => void;
};

type TxHelpers = {
  setEventHandlers: (eventHandlers: TxEventHandlers) => void;
};

const TransactionsContext = createContext<
  [TxState, TxDispatch, TxHelpers] | undefined
>(undefined);

const useTransactionsContext = (): [TxState, TxDispatch, TxHelpers] => {
  const context = useContext(TransactionsContext);
  if (context === undefined) {
    throw Error('The TransactionsContext has not been defined.');
  }
  return context;
};

interface TransactionsProviderProps extends UIElementProps {}

const TransactionsProvider = (props: TransactionsProviderProps) => {
  const { children } = props;

  const [{ onPending, onCancelled, onCompleted, onFailed }, setEventHandlers] =
    useState<TxEventHandlers>({});

  const lcd = useLCDClient();

  const value = useThunkReducer(transactionsReducer, initialState, (state) => {
    return {
      ...state,
      initialized: true,
      transactions: storage.read(),
    };
  });

  // update the tracking status of the outstanding txs
  useEffect(() => {
    const [state, dispatch] = value;
    if (state.initialized) {
      state.transactions.forEach((transaction) => {
        if (transaction.status === TransactionStatus.Pending) {
          dispatch(trackTxAction(transaction.txHash, lcd));
        }
      });
    }
  }, [value[0].initialized]);

  useEffect(() => {
    const pending = pendingSubject.subscribe((transaction) => {
      if (onPending && transaction.status === TransactionStatus.Pending) {
        onPending(transaction);
      }
    });

    const completed = completedSubject.subscribe((transaction) => {
      if (onCompleted && transaction.status === TransactionStatus.Completed) {
        onCompleted(transaction);
      }
    });

    const cancelled = cancelledSubject.subscribe((transaction) => {
      if (onCancelled && transaction.status === TransactionStatus.Pending) {
        onCancelled(transaction);
      }
    });

    const failed = failedSubject.subscribe((transaction) => {
      if (onFailed && transaction.status === TransactionStatus.Failed) {
        onFailed(transaction);
      }
    });

    return () => {
      pending.unsubscribe();
      completed.unsubscribe();
      cancelled.unsubscribe();
      failed.unsubscribe();
    };
  }, [onCompleted, onCancelled, onFailed, onPending]);

  const newValue = useMemo(() => {
    return [value[0], value[1], { setEventHandlers }] as [
      TxState,
      TxDispatch,
      TxHelpers
    ];
  }, [value, setEventHandlers]);

  return (
    <TransactionsContext.Provider value={newValue}>
      {children}
    </TransactionsContext.Provider>
  );
};

export { TransactionsProvider, useTransactionsContext };
